import { ClientCreation, ClientCreationWithErrors, ErrorClient, ErrorClientType } from '@Arti-zen/package-backoffice'
import { CheckCircle, Close } from '@mui/icons-material'
import Info from 'components/Info'
import { ITableInfinite } from 'components/table/infinite/InfiniteTable.interfaces'
import { Error, InfoRedStyle, Row, ValidButton } from 'modules/product/ProductListTable.definitionFields'
import CancelIcon from '@mui/icons-material/Cancel'
import { Theme } from 'theme/theme'
import IsProfessionalErrorTable from '../errorListTable/IsProfessionalErrorTable'
import PostalCodeErrorTable from '../errorListTable/PostalCodeErrorTable'
import CityErrorTable from '../errorListTable/CityErrorTable'
import TitleErrorTable from '../errorListTable/TitleErrorTable'
import styled from 'styled-components'
import { cleanText } from 'utils/schema.helper'
import { getTitleInName } from '../clients.helper'

type TProps = {
  hasId: boolean
  openForm: (client: ClientCreationWithErrors, index: number) => void
  correctClient: ({
    corrections,
    client,
  }: {
    corrections: Partial<ClientCreation>
    client: ClientCreationWithErrors
  }) => void
  replaceAllErrors: ({
    property,
    condition,
    getCorrections,
  }: {
    property: keyof ClientCreation
    condition?: (client: ClientCreation) => boolean
    getCorrections: (client: ClientCreation, error: ErrorClient | null) => Partial<ClientCreation> | null
  }) => void
  numberOfErrors: Record<keyof ClientCreation, number> | null
  seeErrorsButton: ({ property }: { property: keyof ClientCreation }) => JSX.Element
}

export const definitionFields = ({
  hasId,
  openForm,
  correctClient,
  replaceAllErrors,
  numberOfErrors,
  seeErrorsButton,
}: TProps): ITableInfinite<ClientCreationWithErrors>['definitionFields'] => {
  const getTitleCorrection = (client: ClientCreation) => {
    const lastNameTitle = getTitleInName(client.lastName)
    const firstNameTitle = getTitleInName(client.firstName)

    return lastNameTitle?.title
      ? { ...lastNameTitle, type: 'lastName' }
      : firstNameTitle?.title
        ? { ...firstNameTitle, type: 'firstName' }
        : null
  }

  return [
    ...(hasId
      ? [
          {
            indexName: 'id' as const,
            label: 'ID client',
            onClick: (client, index) => openForm(client, index),
          },
        ]
      : []),
    {
      indexName: 'title',
      label: 'Civilité (title)',
      labelElement: (
        <LabelWrapper>
          <TitleLabel>Civilité</TitleLabel>
          {!!numberOfErrors?.title && (
            <>
              {seeErrorsButton({ property: 'title' })}
              <LabelButton
                onClick={() =>
                  replaceAllErrors({
                    property: 'title',
                    getCorrections: () => ({
                      title: null,
                    }),
                  })
                }
              >
                Supprimer les civilités en erreur
              </LabelButton>
            </>
          )}
          {replaceAllErrors && (
            <LabelButton
              onClick={() => {
                replaceAllErrors({
                  property: 'title',
                  getCorrections: (client) => {
                    const correction = getTitleCorrection(client)
                    if (!correction) return {}
                    return { title: correction.title, [correction.type]: correction.cleanedName }
                  },
                  condition: () => true,
                })
              }}
            >
              Importer les civilités du nom/prénom
            </LabelButton>
          )}
        </LabelWrapper>
      ),
      onClick: (client, index) => openForm(client, index),
      format: (client) => {
        const error = client.errors?.title

        if (error && 'title' in error) {
          return (
            <TitleErrorTable
              currentTitle={error.title}
              correctClient={(corrections) => correctClient({ corrections, client })}
            />
          )
        }

        if (error) {
          return (
            <Row>
              <Error>{client?.title}</Error>
              <Info text={'Un client professionnel ne peut pas avoir de civilité.'} style={InfoRedStyle} />
            </Row>
          )
        }

        // const titleInLastName = getTitleInName(client.lastName)
        // const titleInFirstName = getTitleInName(client.firstName)

        // if (!client.title && (titleInFirstName || titleInLastName)) {
        //   return (
        //     <Row>
        //       <>{client?.title}</>
        //       <ValidButton
        //         onClick={(e) => {
        //           e.preventDefault()
        //           e.stopPropagation()
        //           correctClient({
        //             corrections: {
        //               title: !!titleInLastName ? titleInLastName.title : titleInFirstName?.title,
        //               ...(!!titleInLastName
        //                 ? { lastName: titleInLastName?.cleanedName }
        //                 : { firstName: titleInFirstName?.cleanedName }),
        //             },
        //             client,
        //           })
        //         }}
        //       >
        //         Extraire du {!!titleInLastName ? 'nom' : 'prénom'}
        //       </ValidButton>
        //     </Row>
        //   )
        // }
        return <>{client?.title ? client.title : '_'}</>
      },
    },
    {
      indexName: 'lastName',
      label: 'Nom (lastname)',
      labelElement: (
        <LabelWrapper>
          <TitleLabel>Nom*</TitleLabel>
          {!!numberOfErrors?.lastName && (
            <>
              {seeErrorsButton({ property: 'lastName' })}
              <LabelButton
                onClick={() =>
                  replaceAllErrors({
                    property: 'lastName',
                    getCorrections: (client, error) => {
                      if (error.type === ErrorClientType.UNKNOWN_SYMBOL) {
                        return { lastName: cleanText(error.value) }
                      }
                      if (!!client.firstName) {
                        return { lastName: client.firstName, firstName: null }
                      }
                      return {}
                    },
                  })
                }
              >
                Nettoyer les noms en erreur
              </LabelButton>
            </>
          )}
        </LabelWrapper>
      ),
      onClick: (client, index) => openForm(client, index),
      format: ({ errors, lastName }) => {
        const error = errors?.lastName

        if (error) {
          const errorMessage =
            'property' in error
              ? `Le nom (lastName) "${error.value}" contient au moins un caractère invalide (emoji, caractère spécial...)`
              : "Le client n'a pas de nom (lastName)"
          return (
            <Row>
              <Error>{'value' in error ? error.value : '_'}</Error>
              <Info text={errorMessage} style={InfoRedStyle} />
            </Row>
          )
        }

        return <>{lastName}</>
      },
    },
    {
      indexName: 'firstName',
      label: 'Prénom (firstName)',
      labelElement: (
        <LabelWrapper>
          <TitleLabel>Prénom</TitleLabel>
          {!!numberOfErrors?.firstName && (
            <>
              {seeErrorsButton({ property: 'firstName' })}
              <LabelButton
                onClick={() =>
                  replaceAllErrors({
                    property: 'firstName',
                    // @ts-expect-error
                    getValue: (err) => cleanText(err.text),
                  })
                }
              >
                Nettoyer les prénoms en erreur
              </LabelButton>
            </>
          )}
        </LabelWrapper>
      ),
      onClick: (client, index) => openForm(client, index),
      format: ({ errors, firstName }) => {
        const error = errors?.firstName

        if (error) {
          return (
            <Row>
              <Error>{'value' in error ? error.value : '_'}</Error>
              <Info
                text={`Le prénom (firstName) contient au moins un caractère invalide (emoji, caractère spécial...)`}
                style={InfoRedStyle}
              />
            </Row>
          )
        }

        return <>{firstName ? firstName : '_'}</>
      },
    },
    {
      indexName: 'phone',
      label: 'N° téléphone (phone)',
      labelElement: (
        <LabelWrapper>
          <TitleLabel>N° de téléphone</TitleLabel>
          {!!numberOfErrors?.phone && (
            <>
              {seeErrorsButton({ property: 'phone' })}
              <LabelButton
                onClick={() =>
                  replaceAllErrors({
                    property: 'phone',
                    getCorrections: () => ({ phone: null }),
                  })
                }
              >
                Supprimer les N° de téléphone en erreur
              </LabelButton>
            </>
          )}
        </LabelWrapper>
      ),
      onClick: (client, index) => openForm(client, index),
      format: ({ errors, phone }) => {
        const error = errors?.phone

        if (error && 'phone' in error) {
          return (
            <Row>
              <Error>{error.phone}</Error>
              <Info text={`Le n° de téléphone renseigné a un format incorrect : ${error.phone}`} style={InfoRedStyle} />
            </Row>
          )
        }

        return <>{phone ? phone : '_'}</>
      },
    },
    {
      indexName: 'mobile',
      label: 'N° portable (mobile)',
      labelElement: (
        <LabelWrapper>
          <TitleLabel>N° de portable</TitleLabel>
          {!!numberOfErrors?.mobile && (
            <>
              {seeErrorsButton({ property: 'mobile' })}
              <LabelButton
                onClick={() =>
                  replaceAllErrors({
                    property: 'mobile',
                    getCorrections: () => ({ mobile: null }),
                  })
                }
              >
                Supprimer les N° de portable en erreur
              </LabelButton>
            </>
          )}
        </LabelWrapper>
      ),
      onClick: (client, index) => openForm(client, index),
      format: ({ errors, mobile }) => {
        const error = errors?.mobile

        if (error && 'mobile' in error) {
          return (
            <Row>
              <Error>{error.mobile}</Error>
              <Info text={`Le n° de portable renseigné a un format incorrect : ${error.mobile}`} style={InfoRedStyle} />
            </Row>
          )
        }

        return <>{mobile ? mobile : '_'}</>
      },
    },
    {
      indexName: 'email',
      label: 'Email (email)',
      labelElement: (
        <LabelWrapper>
          <TitleLabel>Email</TitleLabel>
          {!!numberOfErrors?.email && (
            <>
              {' '}
              {seeErrorsButton({ property: 'email' })}
              <LabelButton
                onClick={() =>
                  replaceAllErrors({
                    property: 'email',
                    getCorrections: () => ({ email: null }),
                  })
                }
              >
                Supprimer les adresses email en erreur
              </LabelButton>
            </>
          )}
        </LabelWrapper>
      ),
      onClick: (client, index) => openForm(client, index),
      format: ({ errors, email }) => {
        const error = errors?.email

        if (error && 'email' in error) {
          return (
            <Row>
              <Error>{error.email}</Error>
              <Info text={`L'email renseigné a un format incorrect : ${error.email}`} style={InfoRedStyle} />
            </Row>
          )
        }

        return <>{email ? email : '_'}</>
      },
    },
    {
      indexName: 'address',
      label: 'Adresse (address)',
      labelElement: (
        <LabelWrapper>
          <TitleLabel>Adresse</TitleLabel>
          {!!numberOfErrors?.address && (
            <>
              {seeErrorsButton({ property: 'address' })}
              <LabelButton
                onClick={() =>
                  replaceAllErrors({
                    property: 'address',
                    // @ts-expect-error
                    getValue: (err) => ('expectedAddress' in err ? err.expectedAddress : cleanText(err.text)),
                  })
                }
              >
                Nettoyer les adresses en erreur
              </LabelButton>
            </>
          )}
        </LabelWrapper>
      ),
      onClick: (client, index) => openForm(client, index),
      format: ({ errors, address }) => {
        const error = errors?.address

        if (error) {
          return (
            <Row>
              <Error>{'value' in error ? error.value : '_'}</Error>
              <Info
                text={`L'adresse (address) contient au moins un caractère invalide (emoji, caractère spécial...)`}
                style={InfoRedStyle}
              />
            </Row>
          )
        }

        return <>{address ? address : '_'}</>
      },
    },
    {
      indexName: 'additionalAddress',
      label: 'Complément adresse (additionalAddress)',
      labelElement: (
        <LabelWrapper>
          <TitleLabel>Complément d'adresse</TitleLabel>
          {!!numberOfErrors?.additionalAddress && (
            <>
              {seeErrorsButton({ property: 'additionalAddress' })}
              <LabelButton
                onClick={() =>
                  replaceAllErrors({
                    property: 'additionalAddress',
                    // @ts-expect-error
                    getValue: (err) => cleanText(err.text),
                  })
                }
              >
                Nettoyer les compléments d'adresse en erreur
              </LabelButton>
            </>
          )}
        </LabelWrapper>
      ),
      onClick: (client, index) => openForm(client, index),
      format: ({ errors, additionalAddress }) => {
        const error = errors?.additionalAddress

        if (error) {
          return (
            <Row>
              <Error>{'value' in error ? error.value : '_'}</Error>
              <Info
                text={`Le complément d'adresse (additionalAddress) contient au moins un caractère invalide (emoji, caractère spécial...)`}
                style={InfoRedStyle}
              />
            </Row>
          )
        }

        return <>{additionalAddress ? additionalAddress : '_'}</>
      },
    },
    {
      indexName: 'postalCode',
      label: 'Code postal (postalCode)',
      labelElement: (
        <LabelWrapper>
          <TitleLabel>Code postal</TitleLabel>
          {!!numberOfErrors?.postalCode && (
            <>
              {seeErrorsButton({ property: 'postalCode' })}
              <LabelButton
                onClick={() =>
                  replaceAllErrors({
                    property: 'postalCode',
                    getCorrections: (client) => ({ postalCode: client.postalCode }),
                  })
                }
              >
                Ignorer les recommendations
              </LabelButton>
              <LabelButton
                onClick={() =>
                  replaceAllErrors({
                    property: 'postalCode',
                    getCorrections: (client, error) => {
                      let partialClient: Partial<ClientCreation> = {}
                      if (error && 'expectedPostalCode' in error) {
                        partialClient = {
                          postalCode: Array.isArray(error.expectedPostalCode)
                            ? error.expectedPostalCode[0]
                            : error.expectedPostalCode,
                        }
                      }
                      if (error?.type === ErrorClientType.POSTAL_CODE_IN_ADDRESS) {
                        if (!client.city)
                          partialClient = {
                            ...partialClient,
                            city: Array.isArray(error.expectedCity) ? error.expectedCity[0] : error.expectedCity,
                          }
                        partialClient = { ...partialClient, address: error.expectedAddress }
                      }

                      return partialClient
                    },
                  })
                }
              >
                Appliquer les recommendations
              </LabelButton>
            </>
          )}
        </LabelWrapper>
      ),
      onClick: (client, index) => (client.errors.postalCode ? () => {} : openForm(client, index)),
      format: (client) => {
        const error = client.errors?.postalCode

        if (error?.type === ErrorClientType.UNKNOWN_POSTAL_CODE_CITY && 'expectedPostalCode' in error) {
          return (
            <>
              <PostalCodeErrorTable
                correctClient={(corrections) => correctClient({ corrections, client })}
                // @ts-expect-error
                expectedPostalCode={error.expectedPostalCode}
                city={error.city}
                currentPostalCode={error.postalCode}
              />
            </>
          )
        }

        if (error?.type === ErrorClientType.POSTAL_CODE_IN_ADDRESS) {
          return (
            <Row style={{ gap: 5 }}>
              <ValidButton
                style={{ flexDirection: 'column', gap: 0, alignItems: 'center', justifyContent: 'center' }}
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  correctClient({
                    corrections: {
                      postalCode: error.expectedPostalCode,
                      ...(!client.city && { city: error.expectedCity }),
                      address: error.expectedAddress,
                    },
                    client,
                  })
                }}
              >
                <p style={{ fontSize: 10, margin: 0 }}>
                  Récupérer CP {error.expectedCity && !client.city ? 'et ville' : ''} de l'adresse
                </p>
                <p style={{ margin: 0 }}>{error.expectedPostalCode}</p>
              </ValidButton>
              <div
                style={{ color: Theme.colors.blueArtizen }}
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  correctClient({ corrections: { postalCode: null, address: null }, client })
                }}
              >
                <Close color="inherit" fontSize="small" />
              </div>
            </Row>
          )
        }

        return <>{client.postalCode ? client.postalCode : '_'}</>
      },
    },
    {
      indexName: 'city',
      label: 'Ville (city)',
      labelElement: (
        <LabelWrapper>
          <TitleLabel>Ville</TitleLabel>
          {!!numberOfErrors?.city && (
            <>
              {seeErrorsButton({ property: 'city' })}
              <LabelButton
                onClick={() =>
                  replaceAllErrors({
                    property: 'city',
                    getCorrections: (client) => ({ city: client.city }),
                  })
                }
              >
                Ignorer les recommendations
              </LabelButton>
              <LabelButton
                onClick={() =>
                  replaceAllErrors({
                    property: 'city',
                    getCorrections: (client, error) => {
                      let partialClient: Partial<ClientCreation> = {}
                      if (error && 'expectedCity' in error) {
                        partialClient.city = Array.isArray(error.expectedCity)
                          ? error.expectedCity[0]
                          : error.expectedCity
                      }
                      if (error?.type === ErrorClientType.POSTAL_CODE_IN_ADDRESS) {
                        if (client.postalCode)
                          partialClient.postalCode = Array.isArray(error.expectedPostalCode)
                            ? error.expectedPostalCode[0]
                            : error.expectedPostalCode
                        partialClient.address = error.expectedAddress
                      }
                      return partialClient
                    },
                  })
                }
              >
                Appliquer les recommendations
              </LabelButton>
            </>
          )}
        </LabelWrapper>
      ),
      onClick: (client, index) => (client.errors.city ? () => {} : openForm(client, index)),
      format: (client) => {
        const error = client.errors?.city

        if (error?.type === ErrorClientType.UNKNOWN_POSTAL_CODE_CITY && 'expectedCity' in error) {
          return (
            <>
              <CityErrorTable
                correctClient={(corrections) => correctClient({ corrections, client })}
                // @ts-expect-error
                expectedCity={error.expectedCity}
                postalCode={error.postalCode}
                currentCity={error.city}
              />
            </>
          )
        }

        if (error?.type === ErrorClientType.POSTAL_CODE_IN_ADDRESS && 'expectedCity' in error) {
          return (
            <Row style={{ gap: 5 }}>
              <ValidButton
                style={{ flexDirection: 'column', gap: 0, alignItems: 'center', justifyContent: 'center' }}
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  correctClient({
                    corrections: {
                      ...(!client.postalCode && { postalCode: error.expectedPostalCode }),
                      city: error.expectedCity,
                      address: error.expectedAddress,
                    },
                    client,
                  })
                }}
              >
                <p style={{ fontSize: 10, margin: 0 }}>
                  Récupérer {!client.postalCode ? 'CP et' : ''} ville de l'adresse
                </p>
                <p style={{ margin: 0 }}>{error.expectedCity}</p>
              </ValidButton>
              <div
                style={{ color: Theme.colors.blueArtizen }}
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  correctClient({ corrections: { postalCode: null, address: null }, client })
                }}
              >
                <Close color="inherit" fontSize="small" />
              </div>
            </Row>
          )
        }

        return <>{client.city ? client.city : '_'}</>
      },
    },
    {
      indexName: 'isProfessional',
      label: 'Professionel (isProfessional)',
      labelElement: (
        <LabelWrapper>
          <TitleLabel>Client professionnel</TitleLabel>
          {!!numberOfErrors?.isProfessional && (
            <>
              {seeErrorsButton({ property: 'isProfessional' })}
              <LabelButton
                onClick={() =>
                  replaceAllErrors({
                    property: 'isProfessional',
                    getCorrections: (client) => ({ isProfessional: Boolean(client.siren) }),
                  })
                }
              >
                En fonction du SIRET
              </LabelButton>
              <LabelButton
                onClick={() =>
                  replaceAllErrors({
                    property: 'isProfessional',
                    getCorrections: () => ({ isProfessional: false }),
                  })
                }
              >
                Passer en particuliers
              </LabelButton>
              <LabelButton
                onClick={() =>
                  replaceAllErrors({
                    property: 'isProfessional',
                    getCorrections: () => ({ isProfessional: true }),
                  })
                }
              >
                Passer en professionnels
              </LabelButton>
            </>
          )}
        </LabelWrapper>
      ),
      onClick: (client, index) => (client.errors.isProfessional ? () => {} : openForm(client, index)),
      format: (client) => {
        const error = client.errors?.isProfessional
        if (error) {
          return (
            <IsProfessionalErrorTable
              errorMessage={`Professionel "isProfessional" doit être égal à "true" ou à "false"${'isProfessional' in error ? ` : ${error.isProfessional}` : ''}.`}
              correctClient={(corrections) => correctClient({ corrections, client })}
            />
          )
        }

        return (
          <>
            {client.isProfessional ? (
              <div style={{ color: Theme.colors.blueArtizen }}>
                <CheckCircle color="inherit" />
              </div>
            ) : (
              <div style={{ color: 'lightgray' }}>
                <CancelIcon color="inherit" />
              </div>
            )}
          </>
        )
      },
    },
    {
      indexName: 'siren',
      label: 'N° SIRET (siren)',
      labelElement: (
        <LabelWrapper>
          <TitleLabel>N° SIRET</TitleLabel>
          {!!numberOfErrors?.siren && (
            <>
              {seeErrorsButton({ property: 'siren' })}
              <LabelButton
                onClick={() =>
                  replaceAllErrors({
                    property: 'siren',
                    getCorrections: () => ({ siren: null }),
                  })
                }
              >
                Supprimer les SIRET en erreur
              </LabelButton>
            </>
          )}
        </LabelWrapper>
      ),
      onClick: (client, index) => openForm(client, index),
      format: ({ errors, siren, isProfessional }) => {
        const error = errors?.siren

        if (error && 'siren' in error) {
          return (
            <Row>
              <Error>{error.siren}</Error>
              <Info text={`Le N° de SIRET renseigné est incorrect : ${error.siren}.`} style={InfoRedStyle} />
            </Row>
          )
        }

        if (error) {
          return (
            <Row>
              <Error>{siren}</Error>
              <Info
                text={
                  isProfessional
                    ? 'Pour un client professionnel, le N° de SIRET est obligatoire.'
                    : 'Un client non-professionnel ne peut pas avoir de N° de SIRET.'
                }
                style={InfoRedStyle}
              />
            </Row>
          )
        }

        return <>{siren ? siren : '_'}</>
      },
    },
    {
      indexName: 'clientNotes',
      label: 'Notes clients',
      labelElement: (
        <LabelWrapper>
          <TitleLabel>Notes clients</TitleLabel>
          {!!numberOfErrors?.clientNotes && (
            <>
              {' '}
              {seeErrorsButton({ property: 'clientNotes' })}
              <LabelButton
                onClick={() =>
                  replaceAllErrors({
                    property: 'clientNotes',
                    // @ts-expect-error
                    getValue: (err) => cleanText(err.text),
                  })
                }
              >
                Nettoyer les notes en erreur
              </LabelButton>
            </>
          )}
        </LabelWrapper>
      ),
      onClick: (client, index) => openForm(client, index),
      format: ({ errors, clientNotes }) => {
        const error = errors?.clientNotes

        if (error) {
          return (
            <Row>
              <Error>{'value' in error ? error.value : '_'}</Error>
              <Info
                text={`Les notes contiennent au moins un caractère invalide (emoji, caractère spécial...)`}
                style={InfoRedStyle}
              />
            </Row>
          )
        }

        return <>{clientNotes ? clientNotes : '_'}</>
      },
    },
  ]
}

const LabelWrapper = styled.div`
  padding: 10px 5px 5px 5px;
  display: flex;
  flex-direction: column;
  gap: 5px;
  align-self: flex-start;
`

const TitleLabel = styled.h2`
  color: white;
  font-size: 14px;
  font-family: Roboto;
  margin: 0;
  text-transform: none;
`

const LabelButton = styled.div`
  background-color: white;
  color: ${Theme.colors.blueArtizen};
  padding: 5px;
  border-radius: 3px;
  text-transform: none;
  &:hover {
    background-color: ${Theme.colors.greenArtizen};
    color: white;
    cursor: pointer;
  }
`
